import { formatDate, formatDateTime, formatMoney, ListView, PageError, PageLoading, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import type { Owner } from "../../customers";
import { messageFor } from "../../error-copy";
import { isOverdue, statusBadge } from "../../invoices";
import { METHOD, PAYMENT_STATE, purposeOf } from "../../payments";
import styles from "../customers/customers.module.css";

/** The ageing buckets, oldest last, as people say them. */
export const BUCKETS = [
  ["current", "Not yet due"],
  ["days_1_30", "1–30 days"],
  ["days_31_60", "31–60 days"],
  ["days_61_90", "61–90 days"],
  ["days_90_plus", "Over 90 days"],
] as const;

/** A customer's or business's invoices, newest first, each opening its page. */
export function InvoicesPanel({ owner }: { owner: Owner }): ReactNode {
  const format = useCompanyFormat();
  const now = Date.now();
  const list = useCursorList({
    queryKey: ["invoices", "payer", owner],
    fetchPage: (cursor) =>
      ok(api.GET("/v1/admin/billing/invoices", { params: { query: { ...owner, cursor, limit: 10 } } })),
  });
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>Newest first</span>
        <Link to="/invoices" search={owner} className={styles.link}>
          Open in Invoices
        </Link>
      </div>
      <ListView
        caption="Invoices"
        itemName={{ one: "invoice", other: "invoices" }}
        rows={list.rows}
        rowKey={(i) => i.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "No invoices yet", description: "Invoices billed to them show here." }}
        columns={[
          {
            key: "invoice_number",
            label: "Invoice",
            render: (i) => (
              <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className={styles.link}>
                {i.invoice_number ?? "Draft"}
              </Link>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (i) => {
              const [label, tone] = statusBadge(i, now);
              return <Badge tone={tone}>{label}</Badge>;
            },
          },
          {
            key: "due_at",
            label: "Due",
            render: (i) =>
              i.due_at ? (
                <span className={isOverdue(i, now) ? styles.danger : undefined}>{formatDate(i.due_at, format)}</span>
              ) : (
                "—"
              ),
          },
          {
            key: "total_amount",
            label: "Total",
            numeric: true,
            render: (i) => formatMoney(i.total_amount, { ...format, currency: i.currency }),
          },
          {
            key: "amount_due",
            label: "Still owed",
            numeric: true,
            render: (i) => formatMoney(i.amount_due, { ...format, currency: i.currency }),
          },
        ]}
      />
    </div>
  );
}

/** Whose payments: a customer, a business, or one order. */
export type PaymentFilter = Owner | { order_id: string };

/** Payments and refunds, newest first, each opening its page (where it's refunded). */
export function PaymentsPanel({ filter, empty }: { filter: PaymentFilter; empty?: string }): ReactNode {
  const format = useCompanyFormat();
  const list = useCursorList({
    queryKey: ["payments", "for", filter],
    fetchPage: (cursor) => ok(api.GET("/v1/admin/payments", { params: { query: { ...filter, cursor, limit: 10 } } })),
  });
  return (
    <ListView
      caption="Payments"
      itemName={{ one: "payment", other: "payments" }}
      rows={list.rows}
      rowKey={(p) => p.id}
      status={list.status}
      errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
      onRetry={list.refetch}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onLoadMore={list.loadMore}
      filtered={false}
      empty={{ title: "No payments yet", description: empty ?? "Payments and refunds show here." }}
      columns={[
        {
          key: "reference",
          label: "Reference",
          render: (p) => (
            <Link to="/payments/$paymentId" params={{ paymentId: p.id }} className={styles.link}>
              {p.reference}
            </Link>
          ),
        },
        {
          key: "purpose",
          label: "For",
          render: (p) => (p.direction === "outbound" ? "Refund" : purposeOf(p)),
        },
        { key: "method", label: "Method", render: (p) => METHOD[p.method] },
        { key: "receipt_number", label: "Receipt", render: (p) => p.receipt_number ?? "—" },
        {
          key: "amount",
          label: "Amount",
          numeric: true,
          render: (p) => formatMoney(p.amount, { ...format, currency: p.currency }),
        },
        {
          key: "status",
          label: "Status",
          render: (p) => <Badge tone={PAYMENT_STATE[p.status][1]}>{PAYMENT_STATE[p.status][0]}</Badge>,
        },
        {
          key: "created_at",
          label: "When",
          render: (p) => <span className={styles.muted}>{formatDateTime(p.succeeded_at ?? p.created_at, format)}</span>,
        },
      ]}
    />
  );
}

/** What one payer owes, by how overdue (the same buckets as the company's ageing). */
export function PayerAgeing({ owner }: { owner: { kind: "customer" | "business"; id: string } }): ReactNode {
  const format = useCompanyFormat();
  const ageing = useQuery({
    queryKey: ["ageing", owner],
    queryFn: () =>
      owner.kind === "customer"
        ? ok(api.GET("/v1/admin/billing/customers/{id}/ageing", { params: { path: { id: owner.id } } }))
        : ok(api.GET("/v1/admin/billing/businesses/{id}/ageing", { params: { path: { id: owner.id } } })),
  });
  if (ageing.status === "pending") return <PageLoading label="Loading what they owe" />;
  if (ageing.status === "error")
    return <PageError message={messageFor(errorOf(ageing.error))} onRetry={() => ageing.refetch()} />;
  const a = ageing.data;
  const money = (v: string) => formatMoney(v, { ...format, currency: a.currency });
  return (
    <section className={styles.form}>
      <h3 className={styles.title}>How overdue</h3>
      <div className={styles.numbers}>
        {[...BUCKETS, ["not_invoiced", "Not invoiced"] as const].map(([key, label]) => (
          <div key={key} className={styles.number}>
            <span className={styles.muted}>{label}</span>
            <strong>{money(a.totals[key])}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}
